import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BILLING_RETURN, billingKeys } from "@/shared/api/billing";
import { settingsKeys } from "@/shared/api/settings";

/* How a purchase ends. A provider page sends the payer back to `/?billing=success|cancel|
   portal` (billing's CHECKOUT_SUCCESS_URL / CHECKOUT_CANCEL_URL / PORTAL_RETURN_URL); a
   provider checkout over our page (Razorpay) fires BILLING_RETURN instead. The provider's
   webhook reaches billing on its own clock, so a success re-reads the account once more
   after a few seconds.
   Ceiling: a webhook slower than that shows on the next read, not live. */
const WEBHOOK_GRACE_MS = 5000;
const OUTCOMES = ["success", "cancel", "portal"];

export function useCheckoutReturn() {
  const qc = useQueryClient();
  useEffect(() => {
    const reread = () =>
      Promise.all([qc.invalidateQueries({ queryKey: billingKeys.all }), qc.invalidateQueries({ queryKey: settingsKeys.all })]);
    const end = (outcome: string) => {
      window.dispatchEvent(new Event("open-billing"));
      if (outcome === "portal") return void reread(); // a card change or invoice look-up: nothing to announce
      if (outcome === "cancel") return void toast("Checkout cancelled");
      toast.success("Payment received", { description: "Your plan and credits update in a few seconds." });
      // No cleanup: a StrictMode re-run finds the param gone and cannot set it again.
      setTimeout(reread, WEBHOOK_GRACE_MS);
    };

    const url = new URL(window.location.href);
    const outcome = url.searchParams.get("billing");
    if (outcome && OUTCOMES.includes(outcome)) {
      url.searchParams.delete("billing");
      window.history.replaceState(null, "", url);
      end(outcome);
    }

    const onReturn = (e: Event) => end((e as CustomEvent<string>).detail);
    window.addEventListener(BILLING_RETURN, onReturn);
    return () => window.removeEventListener(BILLING_RETURN, onReturn);
  }, [qc]);
}
